/* global performance */
import createStateContext from 'app/utils/createStateContext';
import SubscribableState, { waitUntil } from 'app/utils/SubscribableState';
import { isShallowEqual } from 'app/utils/object';
import { CancelToken } from 'app/utils/cancelToken';
import {
  LoadingState,
  LoadingStage,
} from 'app/containers/state//LoadingState';
import {
  LoadValueType,
  LoadFunctionType,
} from 'app/containers/state/AutoLoader';
import { CacheKey } from './common';

interface IEntry {
  loadingState: LoadingState<unknown>;
  refCount: number;
  expireTime?: number;
  cancel?: () => void;
  promise?: Promise<unknown>;
}

interface ICacheItem {
  cacheKey: CacheKey;
  args: unknown;
  entry: IEntry;
}

type Cache = ICacheItem[];

interface IState {
  cache: Cache;
}

export type AutoLoaderCache = SubscribableState<IState>;

export const createAutoLoaderCache = (): AutoLoaderCache => new SubscribableState<IState>({ cache: [] });

export const getCachedState = (store: AutoLoaderCache, { cacheKey, args }: {
  cacheKey: CacheKey,
  args: unknown,
}): LoadingState<unknown> => {
  const entry = findEntry(store, cacheKey, args);

  if (entry) {
    return entry.loadingState;
  } else {
    // If there is no entry yet, assume we will start loading it
    return {
      stage: LoadingStage.Loading,
      value: undefined,
      error: undefined,
    };
  }
};

export const addReference = (store: AutoLoaderCache, cacheKey: CacheKey, args: unknown, cacheTimeout: number) => {
  updateEntry(store, cacheKey, args, entry => {
    const refCount = entry.refCount + 1;

    return {
      ...entry,
      refCount,
      expireTime: refCount > 0 ? undefined : entry.expireTime,
    };
  }, {
    skipNotify: true,
    // This is the only place where a new entry can be created, to ensure
    // that we never create an entry that isn't being reference-counted.
    createIfNotFound: true,
  });

  const releaseReference = () => {
    updateEntry(store, cacheKey, args, entry => {
      const refCount = entry.refCount - 1;
      const expireTime = refCount <= 0 ? (performance.now() + cacheTimeout) : undefined;

      return {
        ...entry,
        refCount,
        expireTime,
      };
    }, { skipNotify: true });

    removeExpiredEntries(store);
  };

  return releaseReference;
};

export const loadIntoCache = <LoadType extends LoadFunctionType>(store: AutoLoaderCache, {
  cacheKey,
  cacheTimeout,
  args,
  load,
}: {
  cacheKey: CacheKey,
  cacheTimeout: number,
  args: unknown,
  load: LoadType,
}): Promise<LoadValueType<LoadType>> => {
  const foundEntry = findEntry(store, cacheKey, args);

  let loadPromise = foundEntry ? foundEntry.promise : undefined;

  if (!loadPromise) {
    const { cancelToken, cancel } = CancelToken.create();

    loadPromise = (async () => {
      try {
        const value = await load(args, cancelToken);

        if (!cancelToken.isCancelled()) {
          updateEntry(store, cacheKey, args, entry => ({
            ...entry,
            loadingState: {
              stage: LoadingStage.Success,
              value,
              error: undefined,
            },
          }));
        }

        return value;
      } catch (error) {
        if (!cancelToken.isCancelled()) {
          updateEntry(store, cacheKey, args, entry => ({
            ...entry,
            loadingState: {
              stage: LoadingStage.Error,
              value: undefined,
              error,
            },
          }));
        }

        throw error;
      }
    })();

    updateEntry(store, cacheKey, args, entry => ({
      ...entry,
      loadingState: {
        stage: LoadingStage.Loading,
        value: undefined,
        error: undefined,
      },
      cancel,
      promise: loadPromise,
    }), {
      // Skip notify to avoid unnecessary re-render of components (since
      // getCachedState already defaults to the loading state anyway).
      skipNotify: true,
    });
  }

  return loadPromise as Promise<LoadValueType<LoadType>>;
};

export const clearResult = (store: AutoLoaderCache, cacheKey: CacheKey, args: unknown) => {
  updateEntry(store, cacheKey, args, entry => ({
    ...entry,
    loadingState: {
      stage: LoadingStage.Idle,
      value: undefined,
      error: undefined,
    },
  }));
};

export const clearPromiseForReload = (store: AutoLoaderCache, cacheKey: CacheKey, args: unknown) => {
  updateEntry(store, cacheKey, args, entry => {
    if (entry.promise && entry.cancel) {
      entry.cancel();
    }

    return {
      ...entry,
      promise: undefined,
      cancel: undefined,
      loadingState: {
        stage: LoadingStage.Loading,
        value: undefined,
        error: undefined,
      },
    };
  });
};

export const clearCache = (store: AutoLoaderCache) => {
  const cache = getCacheRaw(store);

  // Cancel all loads
  for (const { entry } of cache) {
    if (entry.cancel) {
      entry.cancel();
    }
  }

  store.setState({ cache: [] });
};

export const waitForPending = (store: AutoLoaderCache, cancelToken?: CancelToken) =>
  waitUntil(store, () => !hasPending(store), cancelToken);

export const hasPending = (store: AutoLoaderCache) => getLoadingEntries(store).length > 0;

// Private helpers
const getLoadingEntries = (store: AutoLoaderCache) => {
  const cache = getCache(store);
  return cache.filter(({ entry }) => (
    entry.loadingState.stage === LoadingStage.Loading
  ));
};

const findEntry = (store: AutoLoaderCache, targetCacheKey: CacheKey, targetArgs: unknown): IEntry | undefined => {
  const cache = getCache(store);

  const entryIndex = findEntryIndex(cache, targetCacheKey, targetArgs);

  if (entryIndex < 0) {
    return undefined;
  } else {
    return cache[entryIndex].entry;
  }
};

const findEntryIndex = (cache: Cache, targetCacheKey: CacheKey, targetArgs: unknown): number => {
  return cache.findIndex(
    ({ cacheKey, args }) => cacheKey === targetCacheKey && isShallowEqual(args, targetArgs),
  );
};

const updateEntry = (
  store: AutoLoaderCache,
  targetCacheKey: CacheKey,
  targetArgs: unknown,
  updater: (entry: IEntry) => IEntry,
  options: {
    skipNotify?: boolean,
    createIfNotFound?: boolean,
  } = {},
) => {
  const {
    skipNotify = false,
    createIfNotFound = false,
  } = options;

  const cache = getCache(store);

  const entryIndex = findEntryIndex(cache, targetCacheKey, targetArgs);
  const noEntryFound = entryIndex < 0;

  if (noEntryFound && !createIfNotFound) {
    return;
  }

  // If entry isn't found, create a new entry
  const sliceIndex = noEntryFound ? cache.length : entryIndex;
  const cacheItem: ICacheItem = noEntryFound ? {
    cacheKey: targetCacheKey,
    args: targetArgs,
    entry: {
      loadingState: {
        stage: LoadingStage.Loading,
        value: undefined,
        error: undefined,
      },
      // The defaults for `refCount` and `expireTime` don't matter in
      // practice, because they'll be overriden in `addReference()`
      refCount: 0,
      expireTime: performance.now(),
    },
  } : cache[entryIndex];

  store.setState({
    cache: [
      ...cache.slice(0, sliceIndex),
      {
        ...cacheItem,
        entry: updater(cacheItem.entry),
      },
      ...cache.slice(sliceIndex + 1),
    ],
  }, { skipNotify });
};

const removeExpiredEntries = (store: AutoLoaderCache) => {
  const cache = getCacheRaw(store);

  const now = performance.now();

  store.setState({
    cache: cache.filter(({ entry }) => {
      const isExpired = entry.expireTime !== undefined && entry.expireTime <= now;

      if (isExpired && entry.cancel) {
        entry.cancel();
      }

      return !isExpired;
    }),
  }, { skipNotify: true });
};

const getCache = (store: AutoLoaderCache) => {
  // Make sure the cache is always clean before using it.
  removeExpiredEntries(store);

  return getCacheRaw(store);
};

const getCacheRaw = (store: AutoLoaderCache) => store.state.cache;

export const {
  Provider: CacheProvider,
  useStore: useCache,
} = createStateContext<IState>();
